import { useState, useCallback } from 'react';

import {
  LearnMoreSearch,
  type LearnMoreSearchResponse,
  type LearnMoreSearchSuggestedArticle,
} from '@/api/openApi';

export type { LearnMoreSearchResponse, LearnMoreSearchSuggestedArticle };

interface UseLearnMoreSearchReturn {
  query: string;
  setQuery: (query: string) => void;
  search: (recaptchaToken: string) => Promise<void>;
  isLoading: boolean;
  response: LearnMoreSearchResponse | null;
  error: string | null;
  clearResults: () => void;
}

const useLearnMoreSearch = (): UseLearnMoreSearchReturn => {
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState<LearnMoreSearchResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const search = useCallback(
    async (recaptchaToken: string) => {
      if (!query.trim()) {
        return;
      }

      setIsLoading(true);
      setError(null);
      setResponse(null);

      try {
        const data = await LearnMoreSearch({
          question: query,
          recaptchaToken,
        });
        setResponse(data);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Something went wrong');
      } finally {
        setIsLoading(false);
      }
    },
    [query],
  );

  const clearResults = useCallback(() => {
    setResponse(null);
    setError(null);
    setQuery('');
  }, []);

  return {
    query,
    setQuery,
    search,
    isLoading,
    response,
    error,
    clearResults,
  };
};

export default useLearnMoreSearch;
